import {
  PLAN_FAMILIES,
  PLAN_WHEN,
  type CompanyPlanDto,
  type PlanChoice,
  type PlanFamily,
  type PlanItemDto,
  type PlanWhen,
} from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../../lib/api';
import { navigate } from '../../../lib/router';
import { useTenant, useTenantQuery } from '../../../lib/useTenant';
import { Card, ErrorText } from '../../../ui';
import { useSetupProgress } from '../Setup';
import { ItemBase } from './Labels';

/** Le tre parti del piano (ADR 0090), nell'ordine in cui si leggono. */
const FAMILY: Record<PlanFamily, { nome: string; breve: string; base: 'legge' | 'crescere' | 'prassi'; dot: string; chip: string }> = {
  legge: { nome: 'Ciò che chiede la legge', breve: 'legge', base: 'legge', dot: 'bg-violet-400', chip: 'bg-violet-100 text-violet-700' },
  prassi: { nome: 'Le buone prassi del settore', breve: 'buone prassi', base: 'prassi', dot: 'bg-sky-300', chip: 'bg-sky-100 text-sky-800' },
};
const FATICA: Record<PlanItemDto['fatica'], string> = { bassa: 'Poco lavoro', media: 'Lavoro medio', alta: 'Molto lavoro' };
const WHEN: Record<PlanWhen, { nome: string; d: string }> = {
  '30': { nome: 'Entro 30 giorni', d: 'Si parte da qui' },
  '90': { nome: 'Entro 3 mesi', d: 'Quando le prime sono avviate' },
  '365': { nome: 'Entro l’anno', d: 'Il resto, con calma' },
};
const DARK = 'rounded-3xl bg-[#0d1220] p-5 text-[#eef2ff] md:p-7';

/** Euro in parole brevi: «45 mila €», «1,84 mln €». */
export function euro(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toLocaleString('it-IT', { maximumFractionDigits: 2 })} mln €`;
  if (n >= 1000) return `${Math.round(n / 1000).toLocaleString('it-IT')} mila €`;
  return `${Math.round(n).toLocaleString('it-IT')} €`;
}

/** Somma delle forbici di stima. */
export function total(items: PlanItemDto[]): { min: number; max: number } {
  return items.reduce((a, p) => ({ min: a.min + (p.stima?.min ?? 0), max: a.max + (p.stima?.max ?? 0) }), { min: 0, max: 0 });
}

const points = (n: number, ricavi: number) => ((n / ricavi) * 100).toLocaleString('it-IT', { maximumFractionDigits: 1 });

type Choose = (p: PlanItemDto, scelta: PlanChoice | null, motivo?: string) => void;

/**
 * «La tua azienda» (ADR 0042, 0090): il piano del consulente per arrivare all'azienda ideale. Prima ciò che chiede la
 * legge, poi le buone prassi del settore (ADR 0171: gli obiettivi del titolare non esistono più). Su ogni proposta il titolare dice «Sì,
 * adesso», «Più avanti» o «No, perché…» (il perché resta come decisione); ciò che chiede la legge non si scarta. In
 * fondo il calendario a 30, 90 e 365 giorni con le proposte scelte adesso.
 */
export function CompanyPlanPage() {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const key = ['tenant', tenant.id, 'plan'];
  const plan = useTenantQuery<CompanyPlanDto>(['plan'], '/onboarding/plan', { refetchInterval: (d) => (d?.generating ? 4000 : false) });
  const progress = useSetupProgress().data;
  const choose = useMutation({
    mutationFn: (body: { id: string; scelta: PlanChoice | null; motivo?: string }) => api(t('/onboarding/plan/choice'), { body }),
    // La scelta si vede subito; il server la registra dietro.
    onMutate: (body) => {
      qc.setQueryData<CompanyPlanDto>(key, (old) =>
        old ? { ...old, proposte: old.proposte.map((p) => (p.id === body.id ? { ...p, scelta: body.scelta, motivo: body.scelta === 'no' ? (body.motivo ?? null) : null } : p)) } : old,
      );
    },
    onError: () => void qc.invalidateQueries({ queryKey: key }),
  });
  const retry = useMutation({
    mutationFn: () => api(t('/onboarding/plan/retry'), { body: {} }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });
  const d = plan.data;
  if (plan.error) return <ErrorText>{errorMessage(plan.error)}</ErrorText>;
  if (!d) return null;
  if (!d.pronta)
    return (
      <Card className="p-5">
        <h2 className="mb-1 text-base font-semibold">La tua azienda si apre alla fine della configurazione</h2>
        <p className="text-sm text-ink/60">Quando arrivi all’ultimo passo il consulente legge tutto ciò che hai confermato e compone qui il piano per arrivare all’azienda ideale.</p>
      </Card>
    );
  const items = d.proposte;
  const onChoose: Choose = (p, scelta, motivo) => choose.mutate({ id: p.id, scelta, ...(motivo ? { motivo } : {}) });
  const now = items.filter((p) => p.scelta === 'adesso');
  const later = items.filter((p) => p.scelta === 'dopo');
  const no = items.filter((p) => p.scelta === 'no');
  const open = items.length - now.length - later.length - no.length;
  const share = items.length ? (items.length - open) / items.length : 0;
  const law = items.filter((p) => p.famiglia === 'legge');
  const practice = items.filter((p) => p.famiglia === 'prassi');
  const got = total(now);
  const ricavi = d.conto?.ricavi ?? 0;
  const step = (k: string) => progress?.passi.find((s) => s.chiave === k);
  const go = (path?: string) => path && navigate(`/t/${tenant.id}/${path}`);
  const jump = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="space-y-10 pb-10">
      {/* Il goal */}
      <section className="grid overflow-hidden rounded-3xl bg-[#0d1220] text-[#eef2ff] md:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]" aria-labelledby="goal">
        <div className="flex flex-col gap-5 p-6 md:p-7">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-white/55">{d.impresa} · Il piano del consulente</p>
            <h2 id="goal" className="mt-2 text-2xl font-semibold leading-tight md:text-3xl">Dalla tua azienda di oggi alla tua azienda al suo meglio</h2>
            {d.sintesi ? (
              <p className="mt-2 max-w-prose text-sm text-white/75">{d.sintesi}</p>
            ) : d.generating ? (
              <p className="mt-2 flex items-center gap-2 text-sm text-white/75">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/70 border-t-transparent" />
                Leggo tutto ciò che hai confermato e compongo il piano…
              </p>
            ) : null}
          </div>
          {!!d.letto.length && (
            <ul className="flex flex-wrap gap-1.5" aria-label="Cosa ha letto il consulente">
              {d.conto && <li className="rounded-full bg-white/10 px-2.5 py-0.5 text-xs text-white/85">Bilancio{d.conto.esercizio ? ` ${d.conto.esercizio}` : ''}</li>}
              {d.letto.map((x) => (
                <li key={x} className="rounded-full bg-white/10 px-2.5 py-0.5 text-xs text-white/85">
                  {x}
                </li>
              ))}
            </ul>
          )}
          {!!items.length && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {PLAN_FAMILIES.map((f, k) => {
                const list = items.filter((p) => p.famiglia === f);
                const decided = list.filter((p) => p.scelta).length;
                return (
                  <button key={f} className="rounded-2xl bg-white/[0.06] p-3 text-left ring-1 ring-white/10 transition hover:bg-white/10" onClick={() => jump(`parte-${f}`)}>
                    <span className="flex items-baseline justify-between gap-2">
                      <span className="text-xl font-semibold tabular-nums">{list.length}</span>
                      <span className="text-xs text-white/50 tabular-nums">{decided ? `${decided} ${decided === 1 ? 'decisa' : 'decise'}` : ''}</span>
                    </span>
                    <span className="flex items-start gap-1.5 text-xs leading-snug text-white/70">
                      <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${FAMILY[f].dot}`} />
                      {k + 1}. {FAMILY[f].nome}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          {d.error && !items.length && (
            <div className="rounded-2xl bg-white/[0.06] p-3 text-sm ring-1 ring-white/10">
              <p className="mb-2 text-white/80">Non sono riuscito a comporre il piano.</p>
              <button className="rounded-lg bg-white px-3 py-1.5 text-sm font-semibold text-[#0d1220]" disabled={retry.isPending} onClick={() => retry.mutate()}>
                Riprova
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Stage label="Progettata" value={d.stati.progettata.fatti >= d.stati.progettata.totale ? 'Completa' : `${d.stati.progettata.fatti} di ${d.stati.progettata.totale} passi`} share={d.stati.progettata.totale ? d.stati.progettata.fatti / d.stati.progettata.totale : 0} on />
            <Stage label="Collegata" value={d.stati.collegata.totale ? `${d.stati.collegata.fatti} di ${d.stati.collegata.totale} programmi` : 'Dopo l’acquisto'} share={d.stati.collegata.totale ? d.stati.collegata.fatti / d.stati.collegata.totale : 0} />
            <Stage label="Viva" value={d.stati.viva.fatti ? `${d.stati.viva.fatti} di ${d.stati.viva.totale} ruoli con una persona` : 'Mancano le persone'} share={d.stati.viva.totale ? d.stati.viva.fatti / d.stati.viva.totale : 0} />
          </div>
        </div>
        <div className="flex flex-col items-center justify-center gap-4 border-t border-white/10 p-6 md:border-t-0 md:border-l">
          <GoalRing share={share} />
          <div className="w-full max-w-sm text-center" aria-live="polite">
            <p className="text-xs text-white/60">Il piano · {items.length - open} di {items.length} proposte decise</p>
            <p className="mt-1 text-lg font-semibold tabular-nums">
              {items.length ? `${now.length} adesso · ${later.length} più avanti · ${no.length} no` : d.generating ? 'Sto componendo…' : '—'}
            </p>
            {got.max > 0 && (
              <p className="mt-1 text-xs text-white/55">
                Ciò che fai adesso vale +{euro(got.min)}–{euro(got.max)} l’anno{ricavi ? ` (+${points(got.min, ricavi)}–${points(got.max, ricavi)} punti di margine)` : ''}: stime che diventano misure quando l’azienda è collegata.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* 1. Ciò che chiede la legge */}
      {!!law.length && (
        <section id="parte-legge" aria-labelledby="t-legge" className={`scroll-mt-4 ${DARK}`}>
          <DarkHead
            n={1}
            title="Prima ciò che chiede la legge"
            id="t-legge"
            text="Gli adempimenti che valgono per un’azienda come la tua, ciascuno con la norma e la scadenza. Non si scartano: puoi farli adesso o spostarli più avanti."
          />
          <div className="space-y-2.5">
            {law.map((p) => (
              <PlanItem key={p.id} p={p} conto={d.conto} onChoose={onChoose} />
            ))}
          </div>
        </section>
      )}

      {/* 2. Le buone prassi */}
      {!!practice.length && (
        <section id="parte-prassi" aria-labelledby="t-prassi" className={`scroll-mt-4 ${DARK}`}>
          <DarkHead n={2} title="Le buone prassi del settore" id="t-prassi" text="Ciò che le aziende del tuo settore fanno quando lavorano bene, anche se nessuna legge lo chiede. Ognuna con la sua fonte." />
          <div className="space-y-2.5">
            {practice.map((p) => (
              <PlanItem key={p.id} p={p} conto={d.conto} onChoose={onChoose} />
            ))}
          </div>
        </section>
      )}

      {/* Il calendario */}
      {!!items.length && <Calendar now={now} />}

      {/* Il conto economico */}
      {d.conto && !!items.length && (
        <section aria-labelledby="t-conto">
          <SectionHead
            eyebrow="Dove agisce"
            title="Il piano sul conto economico"
            id="t-conto"
            text={`Le voci ${d.conto.esercizio ? `del ${d.conto.esercizio} ` : ''}in proporzione ai ricavi e, accanto, le proposte che lavorano su ciascuna. Una voce pesante senza proposte è un punto da guardare.`}
          />
          <Card className="divide-y divide-black/5 p-0">
            {d.conto.voci.map((v) => {
              const on = items.filter((p) => p.voci.includes(v.chiave));
              return (
                <div key={v.chiave} className="grid gap-2 px-4 py-3 text-sm md:grid-cols-[12rem_minmax(0,1fr)_15rem] md:items-center">
                  <div>
                    <p className="font-medium">{v.nome}</p>
                    {v.descrizione && <p className="text-xs text-ink/55">{v.descrizione}</p>}
                  </div>
                  <div>
                    <span className="block h-4 overflow-hidden rounded bg-black/5">
                      <span
                        className={`block h-4 rounded ${v.tipo === 'ricavi' ? 'bg-accent/70' : v.tipo === 'margine' ? 'bg-emerald-600/75' : 'bg-ink/30'}`}
                        style={{ width: `${Math.min(100, (v.importo / d.conto!.ricavi) * 100)}%` }}
                      />
                    </span>
                    <span className="mt-1 block text-xs text-ink/55 tabular-nums">
                      {euro(v.importo)}
                      {v.tipo !== 'ricavi' ? ` · ${points(v.importo, d.conto!.ricavi)}% dei ricavi` : ''}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {PLAN_FAMILIES.map((f) => {
                      const n = on.filter((p) => p.famiglia === f).length;
                      return n ? (
                        <span key={f} className={`rounded-full px-2 py-0.5 text-xs font-semibold ${FAMILY[f].chip}`}>
                          {n} {FAMILY[f].breve}
                        </span>
                      ) : null;
                    })}
                    {!on.length && <span className="text-xs text-ink/40">Nessuna proposta</span>}
                  </div>
                </div>
              );
            })}
            <p className="px-4 py-2 text-xs text-ink/45">Fonte: {d.conto.fonte || 'dati dell’impresa'}</p>
          </Card>
        </section>
      )}

      {/* Le persone */}
      {!!d.ruoli.length && <Roles plan={d} />}

      {/* Il lancio */}
      <section className="grid gap-6 rounded-3xl bg-[#0d1220] p-6 text-[#eef2ff] md:grid-cols-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-white/55">Il lancio</p>
          <h2 className="mt-1 text-2xl font-semibold">Dal piano ai risultati</h2>
          <p className="mt-2 max-w-prose text-sm text-white/70">
            L’azienda ideale è progettata e il piano è scritto. Dopo l’acquisto la configurazione operativa porta dentro le persone e collega i programmi: da lì il calendario parte e le stime diventano misure.
          </p>
        </div>
        <ol className="space-y-2 text-sm">
          <Launch done n={1} title="Azienda ideale progettata" sub="Tutti i passi della configurazione" />
          <Launch done={!!items.length && !open} n={2} title="Decidi il piano" sub={`${items.length - open} di ${items.length} proposte decise`} onClick={() => jump(open && law.some((p) => !p.scelta) ? 'parte-legge' : 'parte-obiettivo')} />
          <Launch done={step('collegamenti')?.stato === 'fatto'} n={3} title="Collega i programmi" sub={step('collegamenti')?.dettaglio ?? ''} onClick={() => go(step('collegamenti')?.link)} />
          <Launch done={step('persone')?.stato === 'fatto'} n={4} title="Porta dentro le persone" sub={step('persone')?.dettaglio ?? 'E assegna i ruoli dell’azienda ideale'} onClick={() => go(step('persone')?.link)} />
        </ol>
      </section>
    </div>
  );
}

function SectionHead({ eyebrow, title, id, text }: { eyebrow: string; title: string; id: string; text: string }) {
  return (
    <div className="mb-3 px-1">
      <p className="text-xs font-semibold uppercase tracking-wider text-ink/50">{eyebrow}</p>
      <h2 id={id} className="mt-0.5 text-xl font-semibold">
        {title}
      </h2>
      <p className="mt-1 max-w-prose text-sm text-ink/60">{text}</p>
    </div>
  );
}

/** L'intestazione di una parte del piano, sul pannello blu notte. */
function DarkHead({ n, title, id, text }: { n: number; title: string; id: string; text: string }) {
  return (
    <div className="mb-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-white/55">Il piano · parte {n} di 3</p>
      <h2 id={id} className="mt-1 text-2xl font-semibold leading-tight">
        {title}
      </h2>
      <p className="mt-1.5 max-w-prose text-sm text-white/70">{text}</p>
    </div>
  );
}

function Stage({ label, value, share, on = false }: { label: string; value: string; share: number; on?: boolean }) {
  return (
    <div className="rounded-2xl bg-white/[0.06] px-3 py-2 ring-1 ring-white/10">
      <p className="text-xs text-white/60">{label}</p>
      <p className="text-sm font-semibold">{value}</p>
      <span className="mt-1.5 block h-1 overflow-hidden rounded-full bg-white/10">
        <span className={`block h-1 rounded-full ${on ? 'bg-[#5fe0b0]' : 'bg-[#8fb0ff]'}`} style={{ width: `${Math.max(3, Math.round(share * 100))}%` }} />
      </span>
    </div>
  );
}

/** Il cerchio del piano: si chiude man mano che il titolare decide le proposte. */
function GoalRing({ share }: { share: number }) {
  const r = 70;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 200 200" className="h-48 w-48" role="img" aria-label={`Piano deciso al ${Math.round(share * 100)}%`}>
      <defs>
        <radialGradient id="brain" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="35%" stopColor="#8fb0ff" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#8fb0ff" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="ring" x1="0" x2="1">
          <stop offset="0%" stopColor="#8fb0ff" />
          <stop offset="100%" stopColor="#5fe0b0" />
        </linearGradient>
      </defs>
      <circle cx="100" cy="100" r="46" fill="url(#brain)" className="motion-safe:animate-pulse" />
      <circle cx="100" cy="100" r="9" fill="#ffffff" />
      <circle cx="100" cy="100" r={r} fill="none" stroke="rgba(255,255,255,.1)" strokeWidth="6" />
      <circle
        cx="100"
        cy="100"
        r={r}
        fill="none"
        stroke="url(#ring)"
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray={`${c * share} ${c}`}
        transform="rotate(-90 100 100)"
        style={{ transition: 'stroke-dasharray .6s ease' }}
      />
      <text x="100" y="80" textAnchor="middle" fill="rgba(238,242,255,.7)" fontSize="9" fontWeight="600" letterSpacing="1.5">
        IL PIANO
      </text>
      <text x="100" y="130" textAnchor="middle" fill="#ffffff" fontSize="13" fontWeight="600">
        DECISO {Math.round(share * 100)}%
      </text>
    </svg>
  );
}

function PlanItem({ p, conto, onChoose }: { p: PlanItemDto; conto: CompanyPlanDto['conto']; onChoose: Choose }) {
  const f = FAMILY[p.famiglia];
  const law = p.famiglia === 'legge';
  const [asking, setAsking] = useState(false);
  const [why, setWhy] = useState('');
  const ok = why.trim().length >= 2;
  const pick = (s: PlanChoice) => {
    setAsking(false);
    onChoose(p, p.scelta === s ? null : s);
  };
  const sendNo = () => {
    if (!ok) return;
    onChoose(p, 'no', why.trim());
    setAsking(false);
  };
  return (
    <article
      className={`grid gap-4 rounded-2xl p-4 ring-1 transition md:grid-cols-[minmax(0,1fr)_16rem] ${
        p.scelta === 'adesso' ? 'bg-white/[0.09] ring-2 ring-[#5fe0b0]/70' : p.scelta === 'no' ? 'bg-white/[0.03] ring-white/[0.06] opacity-60' : 'bg-white/[0.06] ring-white/10'
      }`}
    >
      <div className="min-w-0">
        <div className="mb-1.5 flex flex-wrap items-baseline gap-1.5">
          <span className="-ml-1">
            <ItemBase v={{ base: f.base, riferimento: p.riferimento || null }} />
          </span>
          {p.area && <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs font-medium text-white/70">{p.area}</span>}
          <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs font-medium text-white/70">{FATICA[p.fatica]}</span>
        </div>
        <h3 className="text-base font-semibold">{p.titolo}</h3>
        {p.riferimento && <p className="mt-0.5 text-xs text-white/55">{p.riferimento}</p>}
        {p.coppia && (
          <p className="mt-2 flex flex-wrap items-center gap-2 text-sm font-medium">
            <span className="rounded-lg bg-white/10 px-2.5 py-0.5">{p.coppia.da}</span>
            <span className="text-white/50">⇄</span>
            <span className="rounded-lg bg-white/10 px-2.5 py-0.5">{p.coppia.a}</span>
            <span className={`text-xs font-semibold ${p.coppia.collegati ? 'text-emerald-300' : 'text-amber-300'}`}>{p.coppia.collegati ? '● Collegati' : '● Da collegare'}</span>
          </p>
        )}
        <p className="mt-2 text-sm text-white/85">{p.cosa_fa}</p>
        <p className="mt-1.5 text-sm text-white/65">
          <span className="font-semibold text-white/85">{law ? 'Perché conta:' : 'Cosa ci guadagni:'}</span> {p.perche_conta}
        </p>
        {p.controllo && (
          <dl className="mt-2.5 grid gap-2 text-sm sm:grid-cols-3">
            {[
              ['Avvisa quando', p.controllo.soglia],
              ['Controllo', p.controllo.frequenza],
              ['A chi', p.controllo.a_chi],
            ]
              .filter(([, v]) => v)
              .map(([k, v]) => (
                <div key={k} className="rounded-lg bg-white/[0.05] px-2.5 py-1.5">
                  <dt className="text-xs text-white/50">{k}</dt>
                  <dd>{v}</dd>
                </div>
              ))}
          </dl>
        )}
        {!!p.prima.length && (
          <p className="mt-2 text-sm text-white/65">
            <span className="font-semibold text-white/85">Serve prima:</span> {p.prima.join(' · ')}
          </p>
        )}
        {!!p.da.length && (
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5" aria-label="Da dove nasce">
            <span className="text-xs text-white/45">Dal fascicolo:</span>
            {p.da.map((x) => (
              <span key={x} className="rounded-md bg-white/[0.07] px-1.5 py-0.5 text-xs text-white/70">
                {x}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-3">
        {(p.scadenza || p.stima) && (
          <div className="rounded-xl bg-white/[0.05] px-3 py-2">
            {p.scadenza && (
              <>
                <p className="text-xs text-white/50">Scadenza</p>
                <p className="text-sm font-semibold text-violet-200">{p.scadenza}</p>
              </>
            )}
            {p.stima && (
              <>
                <p className={`text-xs text-white/50 ${p.scadenza ? 'mt-1.5' : ''}`}>Effetto stimato · l’anno</p>
                <p className="text-base font-semibold text-emerald-300 tabular-nums">
                  +{euro(p.stima.min)}–{euro(p.stima.max)}
                  {conto ? <span className="ml-1 text-xs font-medium text-white/45">({points(p.stima.max, conto.ricavi)} punti)</span> : null}
                </p>
                {p.come_stimata && <p className="mt-0.5 text-xs leading-snug text-white/45">{p.come_stimata}</p>}
              </>
            )}
          </div>
        )}
        <div className="mt-auto space-y-2" role="group" aria-label="La tua scelta">
          <div className={`grid gap-1.5 ${law ? 'grid-cols-2' : 'grid-cols-3'}`}>
            <ChoiceButton on={p.scelta === 'adesso'} tone="now" onClick={() => pick('adesso')}>
              Sì, adesso
            </ChoiceButton>
            <ChoiceButton on={p.scelta === 'dopo'} tone="later" onClick={() => pick('dopo')}>
              Più avanti
            </ChoiceButton>
            {!law && (
              <ChoiceButton
                on={p.scelta === 'no' || asking}
                tone="no"
                onClick={() => {
                  if (p.scelta === 'no') return onChoose(p, null);
                  setWhy(p.motivo ?? '');
                  setAsking((x) => !x);
                }}
              >
                No, perché…
              </ChoiceButton>
            )}
          </div>
          {p.scelta === 'adesso' && <p className="text-xs text-emerald-300">Nel calendario: {WHEN[p.quando].nome.toLowerCase()}</p>}
          {p.scelta === 'dopo' && <p className="text-xs text-white/55">La riprendiamo più avanti: resta nel piano.</p>}
          {p.scelta === 'no' && p.motivo && !asking && <p className="text-xs text-white/60">Non la fai perché {p.motivo}</p>}
          {asking && (
            <form
              className="space-y-1.5"
              onSubmit={(e) => {
                e.preventDefault();
                sendNo();
              }}
            >
              <label className="block text-xs text-white/60" htmlFor={`no-${p.id}`}>
                Perché no? Il consulente lo tiene come una tua decisione.
              </label>
              <textarea
                id={`no-${p.id}`}
                autoFocus
                rows={2}
                maxLength={1000}
                value={why}
                onChange={(e) => setWhy(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendNo();
                  }
                }}
                placeholder="Per esempio: lo fa già un fornitore, non ci serve per ora…"
                className="w-full resize-none rounded-lg bg-white/10 px-2.5 py-1.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-white/40"
              />
              <div className="flex justify-end gap-2">
                <button type="button" className="px-2 py-1 text-xs text-white/55 hover:text-white" onClick={() => setAsking(false)}>
                  Annulla
                </button>
                <button type="submit" disabled={!ok} className="rounded-lg bg-white px-3 py-1 text-xs font-semibold text-[#0d1220] disabled:opacity-40">
                  Conferma il no
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </article>
  );
}

function ChoiceButton({ on, tone, onClick, children }: { on: boolean; tone: 'now' | 'later' | 'no'; onClick: () => void; children: string }) {
  const active = { now: 'bg-[#5fe0b0] text-[#0d1220] ring-[#5fe0b0]', later: 'bg-[#8fb0ff] text-[#0d1220] ring-[#8fb0ff]', no: 'bg-white/80 text-[#0d1220] ring-white/80' }[tone];
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={`rounded-lg px-2 py-1.5 text-xs font-semibold ring-1 transition ${on ? active : 'text-white/85 ring-white/20 hover:bg-white/10'}`}
    >
      {children}
    </button>
  );
}

/** Il calendario: le proposte scelte «adesso», entro 30, 90 o 365 giorni secondo ciò che serve prima. */
function Calendar({ now }: { now: PlanItemDto[] }) {
  return (
    <section aria-labelledby="t-calendario" className={DARK}>
      <div className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-white/55">Il calendario</p>
        <h2 id="t-calendario" className="mt-1 text-2xl font-semibold leading-tight">
          Cosa fai e quando
        </h2>
        <p className="mt-1.5 max-w-prose text-sm text-white/70">
          {now.length
            ? 'Le proposte che hai scelto adesso, messe in fila: prima ciò che scade e ciò che serve alle altre.'
            : 'Qui si mettono in fila le proposte che scegli «Sì, adesso».'}
        </p>
      </div>
      <ol className="grid gap-3 md:grid-cols-3">
        {PLAN_WHEN.map((w, k) => {
          const list = now.filter((p) => p.quando === w);
          return (
            <li key={w} className="rounded-2xl bg-white/[0.05] p-3 ring-1 ring-white/10">
              <div className="mb-2 flex items-center gap-2">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/15 text-xs font-bold">{k + 1}</span>
                <span>
                  <span className="block text-sm font-semibold">{WHEN[w].nome}</span>
                  <span className="block text-xs text-white/50">{WHEN[w].d}</span>
                </span>
              </div>
              {list.length ? (
                <ul className="space-y-1.5">
                  {list.map((p) => (
                    <li key={p.id} className="rounded-lg bg-white/[0.06] px-2.5 py-1.5 text-sm">
                      <span className="flex items-start gap-1.5">
                        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${FAMILY[p.famiglia].dot}`} />
                        <span className="min-w-0">
                          {p.titolo}
                          {(p.scadenza || p.prima.length > 0) && (
                            <span className="block text-xs text-white/50">{[p.scadenza, p.prima.length ? `dopo: ${p.prima.join(', ')}` : null].filter(Boolean).join(' · ')}</span>
                          )}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-1 text-xs text-white/40">Ancora niente</p>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function Roles({ plan }: { plan: CompanyPlanDto }) {
  const [i, setI] = useState(0);
  const r = plan.ruoli[Math.min(i, plan.ruoli.length - 1)]!;
  return (
    <section aria-labelledby="t-ruoli">
      <SectionHead
        eyebrow="Il piano portato a terra"
        title="Ogni persona preparata a decidere bene"
        id="t-ruoli"
        text="Ogni ruolo riceve la sua parte del piano: la giornata, cosa conosce e le decisioni che il sistema gli proporrà, con i dati. Finché l’azienda non è collegata sono modelli costruiti dalle procedure del ruolo."
      />
      <div className="mb-3 flex flex-wrap gap-1.5 px-1" role="tablist">
        {plan.ruoli.map((x, k) => (
          <button
            key={x.ruolo}
            role="tab"
            aria-selected={k === i}
            className={`rounded-full px-3 py-1 text-sm ring-1 transition ${k === i ? 'bg-ink text-paper ring-ink' : 'ring-black/10 hover:ring-black/25'}`}
            onClick={() => setI(k)}
          >
            {x.ruolo}
          </button>
        ))}
      </div>
      <div className="grid gap-3 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <Card className="p-4">
          <h3 className="mb-3 text-sm font-semibold">La giornata del ruolo {r.ruolo}</h3>
          <ul className="space-y-2.5">
            {r.giornata.map((g) => (
              <li key={`${g.quando}${g.cosa}`} className="grid grid-cols-[3.5rem_minmax(0,1fr)] gap-2 text-sm">
                <span className="text-ink/50 tabular-nums">{g.quando}</span>
                <span>
                  {g.cosa}
                  {g.dettaglio && <span className="block text-xs text-ink/50">{g.dettaglio}</span>}
                </span>
              </li>
            ))}
          </ul>
          {r.conosce && (
            <p className="mt-3 text-sm text-ink/60">
              <span className="font-semibold text-ink">Conosce</span> {r.conosce}
            </p>
          )}
        </Card>
        <Card className="p-4">
          <h3 className="mb-3 text-sm font-semibold">Una decisione che il sistema gli proporrà</h3>
          <div className="rounded-2xl bg-blue-50 p-4">
            <p className="text-xs font-semibold text-accent">Il sistema propone</p>
            <p className="mt-1 text-base font-semibold">{r.decisione.titolo}</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-ink/65">
              {r.decisione.perche.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
            {r.decisione.guadagno && <p className="mt-2 text-sm font-semibold text-emerald-700">{r.decisione.guadagno}</p>}
            {r.decisione.dati && <p className="mt-2 text-xs text-ink/45">Dati: {r.decisione.dati}</p>}
          </div>
        </Card>
      </div>
    </section>
  );
}

function Launch({ n, title, sub, done, onClick }: { n: number; title: string; sub: string; done: boolean; onClick?: () => void }) {
  const body = (
    <>
      <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${done ? 'bg-[#5fe0b0] text-[#0d1220]' : 'bg-white/15'}`}>{done ? '✓' : n}</span>
      <span className="min-w-0">
        <span className="block font-medium">{title}</span>
        {sub && <span className="block text-xs text-white/60">{sub}</span>}
      </span>
    </>
  );
  return (
    <li>
      {onClick && !done ? (
        <button className="flex w-full items-center gap-3 rounded-xl bg-white/[0.06] px-3 py-2.5 text-left transition hover:bg-white/10" onClick={onClick}>
          {body}
        </button>
      ) : (
        <div className="flex items-center gap-3 rounded-xl bg-white/[0.06] px-3 py-2.5">{body}</div>
      )}
    </li>
  );
}
